import { type FormEvent, useEffect, useRef, useState } from "react";
import { useRole } from "../../components/AppShell";
import { PlusIcon, SearchIcon } from "../../components/icons";
import { Empty, ListSkeleton, useConfirm, useToast } from "../../components/ui";
import { ApiError } from "../../lib/api";
import {
  useDeleteSalesPoint,
  useImportSalesPoints,
  useSalesPointProvinces,
  useSalesPoints,
  useSaveSalesPoint,
} from "../../lib/queries";
import type { SalesPoint } from "../../lib/types";
import { CompanyTabs } from "../settings/CompanyPage";
import { POINTS_CSV_TEMPLATE, readPointsCsv, type SalesPointInput } from "./points-csv";

const KIND_LABEL: Record<SalesPoint["kind"], string> = {
  store: "Sucursal propia",
  reseller: "Distribuidor autorizado",
  service: "Centro de servicio",
};

/** Costa Rica's provinces, suggested in the form (any other value is accepted). */
const PROVINCES = ["San José", "Alajuela", "Cartago", "Heredia", "Guanacaste", "Puntarenas", "Limón"];

const useCanEdit = () => {
  const role = useRole();
  return role === "owner" || role === "admin";
};

export function SalesPointsPage() {
  const [q, setQ] = useState("");
  const [province, setProvince] = useState("");
  const list = useSalesPoints(q, province);
  const provinces = useSalesPointProvinces();
  const canEdit = useCanEdit();
  const [editing, setEditing] = useState<SalesPoint | "new" | null>(null);
  const [importing, setImporting] = useState(false);
  const items = list.data?.items ?? [];
  const filtered = !!(q || province);
  const groups = new Map<string, SalesPoint[]>();
  for (const p of items)
    groups.set(p.province ?? "Sin provincia", [...(groups.get(p.province ?? "Sin provincia") ?? []), p]);

  return (
    <div className="page">
      <div className="page-head">
        <h1>Empresa</h1>
      </div>
      <CompanyTabs />
      <div className="page-head">
        <label className="search-field">
          <SearchIcon />
          <input
            className="input"
            type="search"
            placeholder="Buscar por nombre, cantón o dirección"
            aria-label="Buscar puntos de venta"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </label>
        <select
          className="select products-category"
          aria-label="Provincia"
          value={province}
          onChange={(e) => setProvince(e.target.value)}
        >
          <option value="">Todas las provincias</option>
          {provinces.data?.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
        {canEdit && (
          <div className="products-actions">
            <button className="btn" type="button" onClick={() => setImporting(true)}>
              Importar
            </button>
            <button className="btn btn-primary" type="button" onClick={() => setEditing("new")}>
              <PlusIcon />
              Nuevo punto
            </button>
          </div>
        )}
      </div>
      <p className="muted kb-intro">
        Dónde pueden comprar, retirar o llevar a servicio técnico. La IA los usa cuando un cliente pregunta "¿dónde lo
        consigo?" o "¿hay algo cerca de mí?".
      </p>

      <div className="card">
        {list.isPending ? (
          <ListSkeleton />
        ) : items.length === 0 ? (
          <Empty title={filtered ? "Ningún punto coincide." : "Todavía no hay puntos de venta."}>
            <span>
              {filtered ? "Prueba con otra palabra o provincia." : "Agrégalos uno por uno o impórtalos desde un CSV."}
            </span>
          </Empty>
        ) : (
          [...groups].map(([group, points]) => (
            <section key={group} className="points-group">
              <h3>{group}</h3>
              <ul className="product-list">
                {points.map((p) => (
                  <li key={p.id}>
                    <button type="button" className="product-row points-row" onClick={() => setEditing(p)}>
                      <span className="product-main">
                        <span className="product-name" title={p.name}>
                          {p.name}
                        </span>
                        <span className="product-meta">
                          {p.canton && <span>{p.canton}</span>}
                          {p.address && (
                            <span className="points-address" title={p.address}>
                              {p.address}
                            </span>
                          )}
                        </span>
                        {p.hours && <span className="faint points-hours">{p.hours}</span>}
                      </span>
                      <span className="product-flags">
                        <span className="pill pill-closed">{KIND_LABEL[p.kind]}</span>
                        {!p.visibleToAi && <span className="pill pill-closed">Oculto a la IA</span>}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ))
        )}
      </div>
      {editing && <PointDialog point={editing === "new" ? null : editing} onClose={() => setEditing(null)} />}
      {importing && <ImportPointsDialog onClose={() => setImporting(false)} />}
    </div>
  );
}

type Draft = SalesPointInput & { visibleToAi: boolean };

const EMPTY: Draft = {
  name: "",
  kind: "store",
  province: null,
  canton: null,
  address: null,
  hours: null,
  phone: null,
  mapsUrl: null,
  wazeUrl: null,
  services: null,
  visibleToAi: true,
};

function PointDialog({ point, onClose }: { point: SalesPoint | null; onClose: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const canEdit = useCanEdit();
  const save = useSaveSalesPoint();
  const remove = useDeleteSalesPoint();
  const confirm = useConfirm();
  const toast = useToast();
  const [d, setD] = useState<Draft>(() => (point ? { ...EMPTY, ...point } : EMPTY));
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD((prev) => ({ ...prev, [k]: v }));
  const txt = (k: keyof Draft) => (d[k] as string | null) ?? "";
  useEffect(() => {
    dialog.current?.showModal();
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    try {
      await save.mutateAsync({ id: point?.id, input: d });
      toast(point ? "Punto de venta guardado" : "Punto de venta creado");
      dialog.current?.close();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : "No se pudo guardar.");
    }
  }

  async function onDelete() {
    if (!point) return;
    const ok = await confirm({
      title: `¿Eliminar ${point.name}?`,
      message: "La IA deja de recomendar este punto de venta.",
      confirmLabel: "Eliminar",
    });
    if (!ok) return;
    await remove.mutateAsync(point.id);
    toast("Punto de venta eliminado");
    dialog.current?.close();
  }

  return (
    <dialog ref={dialog} onClose={onClose} aria-labelledby="pt-title" className="points-dialog">
      <form className="dialog-form" onSubmit={onSubmit}>
        <h3 id="pt-title">{point ? point.name : "Nuevo punto de venta"}</h3>
        <fieldset disabled={!canEdit} className="points-fields">
          <div className="field">
            <label htmlFor="pt-name">Nombre</label>
            <input
              className="input"
              id="pt-name"
              placeholder="Ej.: Ferretería El Sol"
              required
              maxLength={200}
              value={d.name}
              onChange={(e) => set("name", e.target.value)}
            />
          </div>
          <div className="two-cols">
            <div className="field">
              <label htmlFor="pt-kind">Tipo</label>
              <select
                className="select"
                id="pt-kind"
                value={d.kind}
                onChange={(e) => set("kind", e.target.value as Draft["kind"])}
              >
                {(Object.keys(KIND_LABEL) as SalesPoint["kind"][]).map((k) => (
                  <option key={k} value={k}>
                    {KIND_LABEL[k]}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="pt-phone">Teléfono</label>
              <input
                className="input"
                id="pt-phone"
                placeholder="Ej.: 2666 1234"
                type="tel"
                maxLength={40}
                value={txt("phone")}
                onChange={(e) => set("phone", e.target.value)}
              />
            </div>
          </div>
          <div className="two-cols">
            <div className="field">
              <label htmlFor="pt-province">Provincia</label>
              <input
                className="input"
                id="pt-province"
                placeholder="Ej.: Guanacaste"
                list="pt-provinces"
                maxLength={80}
                value={txt("province")}
                onChange={(e) => set("province", e.target.value)}
              />
              <datalist id="pt-provinces">
                {PROVINCES.map((p) => (
                  <option key={p} value={p} />
                ))}
              </datalist>
            </div>
            <div className="field">
              <label htmlFor="pt-canton">Cantón</label>
              <input
                className="input"
                id="pt-canton"
                placeholder="Ej.: Liberia"
                maxLength={80}
                value={txt("canton")}
                onChange={(e) => set("canton", e.target.value)}
              />
            </div>
          </div>
          <div className="field">
            <label htmlFor="pt-address">Dirección</label>
            <textarea
              className="textarea"
              id="pt-address"
              rows={2}
              maxLength={500}
              placeholder="200 m norte del parque central"
              value={txt("address")}
              onChange={(e) => set("address", e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="pt-hours">Horario</label>
            <input
              className="input"
              id="pt-hours"
              maxLength={300}
              placeholder="Lunes a sábado, 8 a. m. a 6 p. m."
              value={txt("hours")}
              onChange={(e) => set("hours", e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="pt-services">Qué se puede hacer ahí</label>
            <input
              className="input"
              id="pt-services"
              maxLength={1000}
              placeholder="Recibe equipos para garantía, vende repuestos"
              value={txt("services")}
              onChange={(e) => set("services", e.target.value)}
            />
          </div>
          <div className="two-cols">
            <div className="field">
              <label htmlFor="pt-maps">Google Maps</label>
              <input
                className="input"
                id="pt-maps"
                type="url"
                maxLength={500}
                placeholder="https://maps.app.goo.gl/…"
                value={txt("mapsUrl")}
                onChange={(e) => set("mapsUrl", e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="pt-waze">Waze</label>
              <input
                className="input"
                id="pt-waze"
                type="url"
                maxLength={500}
                placeholder="https://waze.com/ul/…"
                value={txt("wazeUrl")}
                onChange={(e) => set("wazeUrl", e.target.value)}
              />
            </div>
          </div>
          <p className="faint points-hint">
            En Google Maps o Waze, busca el lugar, toca Compartir y copia el enlace. La IA se lo envía al cliente que
            pregunte cómo llegar.
          </p>
          <label className="check">
            <input type="checkbox" checked={d.visibleToAi} onChange={(e) => set("visibleToAi", e.target.checked)} />
            La IA puede recomendar este punto
          </label>
        </fieldset>
        <div className="row">
          {canEdit && point && (
            <button className="btn btn-danger points-delete" type="button" onClick={onDelete}>
              Eliminar
            </button>
          )}
          <button className="btn" type="button" onClick={() => dialog.current?.close()}>
            {canEdit ? "Cancelar" : "Cerrar"}
          </button>
          {canEdit && (
            <button className="btn btn-primary" type="submit" disabled={save.isPending || !d.name.trim()}>
              {save.isPending ? "Guardando…" : "Guardar"}
            </button>
          )}
        </div>
      </form>
    </dialog>
  );
}

function ImportPointsDialog({ onClose }: { onClose: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const importFile = useImportSalesPoints();
  const toast = useToast();
  const [file, setFile] = useState<ReturnType<typeof readPointsCsv> | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    dialog.current?.showModal();
  }, []);

  async function onFile(f: File | undefined) {
    setError(null);
    if (!f) return setFile(null);
    const result = readPointsCsv(await f.text());
    setFile(result);
    if (result.missingName)
      setError('El archivo necesita una columna "nombre". Descarga la plantilla para ver el formato.');
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!file?.items.length) return;
    try {
      const r = await importFile.mutateAsync(file.items);
      toast(`${r.created} puntos nuevos, ${r.updated} actualizados`);
      dialog.current?.close();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo importar.");
    }
  }

  function downloadTemplate() {
    const blob = new Blob([`﻿${POINTS_CSV_TEMPLATE}`], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "plantilla-puntos-de-venta-orbis.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  }

  return (
    <dialog ref={dialog} onClose={onClose} aria-labelledby="pti-title">
      <form className="dialog-form" onSubmit={onSubmit}>
        <h3 id="pti-title">Importar puntos de venta</h3>
        <p className="muted">
          Columnas: nombre (obligatoria), tipo (tienda, distribuidor o centro de servicio), provincia, cantón,
          dirección, horario, teléfono, google maps, waze y servicios. Si importas otra vez, los puntos con el mismo
          nombre y cantón se actualizan.
        </p>
        <div className="field">
          <label htmlFor="pti-file">Archivo CSV</label>
          <input
            className="input"
            id="pti-file"
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => onFile(e.target.files?.[0])}
          />
        </div>
        {file && !file.missingName && (
          <p className="muted">
            {file.items.length} puntos listos
            {file.skipped ? `, ${file.skipped} filas sin nombre se omiten` : ""}.
          </p>
        )}
        <button className="btn btn-ghost btn-sm" type="button" onClick={downloadTemplate}>
          Descargar plantilla
        </button>
        {error && <div className="banner banner-warn">{error}</div>}
        <div className="row">
          <button className="btn" type="button" onClick={() => dialog.current?.close()}>
            Cancelar
          </button>
          <button className="btn btn-primary" type="submit" disabled={importFile.isPending || !file?.items.length}>
            {importFile.isPending ? "Importando…" : "Importar"}
          </button>
        </div>
      </form>
    </dialog>
  );
}
